import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { licenseAPI } from '../services/api';
import {
  ShieldAlert,
  Lock,
  Key,
  ExternalLink,
  Send,
  CheckCircle2,
  X,
  AlertTriangle,
  Building,
  Sparkles,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function LicenseExpiryModal() {
  const { user, isSuperAdmin, isTenantAdmin, licenseScope, licenseStatus } = useAuth();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [licenseKeyInput, setLicenseKeyInput] = useState('');
  const [isActivating, setIsActivating] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);

  const isExpired = Boolean(licenseScope?.isReadOnly || licenseScope?.isExpired);

  useEffect(() => {
    // Only trigger for authenticated non-SuperAdmin tenant users when license is expired
    if (!user || isSuperAdmin || !isExpired) {
      setIsOpen(false);
      return;
    }

    const snoozed = sessionStorage.getItem('license_expiry_alert_dismissed');
    if (!snoozed) {
      // Small timeout to appear smoothly right after login
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [user, isSuperAdmin, isExpired]);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('license_expiry_alert_dismissed', 'true');
  };

  const handleActivateLicense = async (e) => {
    e.preventDefault();
    if (!licenseKeyInput.trim()) {
      toast.error('Please enter a valid cryptographic license key');
      return;
    }

    setIsActivating(true);
    try {
      const res = await licenseAPI.activate({ licenseKey: licenseKeyInput.trim() });
      if (res.data?.success) {
        toast.success('License activated successfully! Full system capabilities restored.');
        setIsOpen(false);
        sessionStorage.removeItem('license_expiry_alert_dismissed');
        window.location.reload();
      } else {
        toast.error(res.data?.message || 'License activation failed');
      }
    } catch (err) {
      console.error('License activation error:', err);
      toast.error(err.response?.data?.message || err.message || 'Invalid or unverifiable license key');
    } finally {
      setIsActivating(false);
    }
  };

  const handleSendRequestToAdmin = async () => {
    setIsRequesting(true);
    try {
      const res = await licenseAPI.requestActivation({
        reason: 'Tenant portal is operating in Read-Only mode due to expired license.'
      });
      if (res.data?.success) {
        setHasRequested(true);
        toast.success('License activation request submitted to your Tenant Administrator!');
      } else {
        toast.error(res.data?.message || 'Failed to submit activation request');
      }
    } catch (err) {
      console.error('Activation request error:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to send request to admin');
    } finally {
      setIsRequesting(false);
    }
  };

  if (!isOpen) return null;

  const expiryDateFormatted = licenseScope?.expiresAt
    ? new Date(licenseScope.expiresAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : 'Recently Expired';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-red-200 overflow-hidden flex flex-col">
        
        {/* Header Ribbon */}
        <div className="bg-gradient-to-r from-red-600 via-rose-600 to-red-700 px-6 py-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-xs flex items-center justify-center text-white shrink-0">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold tracking-tight">Tenant License Expired</h3>
              <p className="text-xs text-red-100 font-medium mt-0.5">
                Portal Restricted to Read-Only Mode
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-red-200 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
            title="Dismiss for this session"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5">
          
          {/* Tenant & Status Summary Card */}
          <div className="p-3.5 bg-red-50/80 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-xs text-red-900 leading-relaxed space-y-1">
              <div className="flex items-center gap-2 font-bold text-red-950">
                <Building className="w-3.5 h-3.5 text-red-700" />
                <span>{user?.client_name || user?.tenant_name || 'Your Organization'}</span>
                {user?.client_code && (
                  <span className="px-1.5 py-0.5 rounded bg-red-200/70 text-[10px] font-mono font-semibold text-red-900">
                    {user.client_code}
                  </span>
                )}
              </div>
              <p>
                The subscription license expired on <span className="font-bold underline">{expiryDateFormatted}</span>.
                All operational modules (Budget, PO, Invoices, Requisitions, Inventory, Approvals) are currently restricted to <span className="font-bold">Read-Only access</span>.
              </p>
              <p className="text-[11px] text-red-800">
                Creation, edits, uploads, and transactional mutations are disabled until a renewed license is activated.
              </p>
            </div>
          </div>

          {/* Conditional Role-Based Action Area */}
          {isTenantAdmin ? (
            /* TENANT ADMIN VIEW: Direct Activation & Marketplace Links */
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                  <Key className="w-3.5 h-3.5 text-blue-600" />
                  <span>Activate Cryptographic License Key</span>
                </h4>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  As a Tenant Administrator, paste your RSA-signed license key below to instantly renew all enterprise modules.
                </p>
              </div>

              <form onSubmit={handleActivateLicense} className="space-y-3">
                <div>
                  <textarea
                    rows={3}
                    required
                    value={licenseKeyInput}
                    onChange={(e) => setLicenseKeyInput(e.target.value)}
                    placeholder="Paste your v2 RSA-signed cryptographic license key here (e.g. v2.eyJ...)"
                    className="w-full p-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-xs font-mono text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <button
                    type="submit"
                    disabled={isActivating || !licenseKeyInput.trim()}
                    className="w-full sm:w-auto flex-1 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {isActivating ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
                    )}
                    <span>{isActivating ? 'Verifying...' : '⚡ Activate License Now'}</span>
                  </button>

                  <a
                    href="https://marketplace.sanglob.com/auth?mode=signin"
                    target="_blank"
                    rel="noreferrer"
                    className="w-full sm:w-auto py-2 px-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl border border-slate-200/80 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <span>Marketplace</span>
                    <ExternalLink className="w-3 h-3 text-slate-500" />
                  </a>
                </div>
              </form>
            </div>
          ) : (
            /* REGULAR TENANT USER VIEW: Request Admin Action */
            <div className="space-y-4">
              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 border border-blue-200 flex items-center justify-center shrink-0 mt-0.5">
                    <Key className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900">Need write / create permissions?</h4>
                    <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">
                      Only Tenant Administrators can activate or renew license keys. You can send an immediate notification to your administrator.
                    </p>
                  </div>
                </div>

                {hasRequested ? (
                  <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-800 text-xs font-semibold">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Activation request sent! Your administrator has been notified.</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendRequestToAdmin}
                    disabled={isRequesting}
                    className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                  >
                    {isRequesting ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    <span>{isRequesting ? 'Submitting Request...' : '🔔 Request Tenant Admin to Activate License'}</span>
                  </button>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200/90 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium">
            <Lock className="w-3.5 h-3.5 text-slate-400" />
            <span>Read-Only Mode Active</span>
          </div>

          <div className="flex items-center gap-2">
            {isTenantAdmin && (
              <button
                type="button"
                onClick={() => { handleClose(); navigate('/settings/license'); }}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline px-2 py-1"
              >
                Go to License Settings
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="py-1.5 px-3.5 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold rounded-lg transition-colors cursor-pointer"
            >
              Continue in Read-Only
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
